import React, { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import QRCodeBlock from './qrCodeBlock'
import authenticationService from 'services/authServices'
import CircleSpinner from 'components/circle-spinner/CircleSpinner'
import { getReturnUrl } from 'utils/authHelpers'
import ErrorBox from 'components/message-boxes/error-box/ErrorBox'

import './bankIdSeContentBlock.scss'

const QRCodeWrapper = ({ qrCodeData } : {qrCodeData: string} ) => {
	
	if (!qrCodeData) {
		return <CircleSpinner></CircleSpinner>
	}

	return <QRCodeBlock qrCodeData={qrCodeData} />
}

const BankIdSeQRContentBlock = () => {
	const [sessionId, setSessionId] = useState('')
	const [qrCodeData, setQRCodeData] = useState('')
	const [remainingTime, setRemainingTime] = useState(300)
	const [remainingTimeMessage, setRemainingTimeMessage] = useState('')
	const [processing, setProcessing] = useState<boolean>(false)
	const [authenticated, setAuthenticated] = useState<boolean>(false)
	const [error, setError] = useState<Error>()
	const countdownTime = Date.now() + 300000
	const { t } = useTranslation()

	useEffect(() => {
		const intervalId = setInterval(() => {
			const timeLeft = Math.floor((countdownTime - new Date().getTime())/1000)
			setRemainingTime(timeLeft > 0 ? timeLeft : 0);
		}, 1000);
		return () => clearInterval(intervalId) // Cleanup
	}, [])

	useEffect(() => {
		const tryContinueBankIdAuth = 
		async () => {
			if (processing || authenticated || !sessionId || error || remainingTime <= 0)
				return;

			try {
				setProcessing(true)
				const completeData =
					await authenticationService.bankIdv2QrCodeContinue(
						sessionId,
						t,
					)

				if(completeData.secret){
					setAuthenticated(true)
					setProcessing(false)
					authenticationService.redirectTo(
						getReturnUrl(),
						completeData.secret,
					)
				}
				else{
					setQRCodeData(completeData.qrData || "")
					setProcessing(false)
				}
				
			} catch (error) {
				setError(new Error(t('BankIDCollectFailure')))
				await tryCancelBankIdAuth(sessionId)
				setProcessing(false)
			}
			setProcessing(false)
		}
		tryContinueBankIdAuth()
	}, [remainingTime])
	
	

	const tryCancelBankIdAuth = 
		async (ref : string) => {
			try {
				await authenticationService.bankIdv2AuthCancel(ref, t)
			} catch (err) {
				setError(new Error(t('BankIdCancelFailure')))
			}
		}

	useEffect(() => {
		const tryStartBankIdAuth = async () => {
			let cancelOrderRef = ''
			try {
				const initiateData =
					await authenticationService.bankIdv2AuthQrInitiate()
				setSessionId(initiateData.sessionId)
				setQRCodeData(initiateData.qrData || '')
				//set qr code data
				cancelOrderRef = initiateData.sessionId
			} catch (err) {
				setError(new Error(t('BankIDStartFailure')))
				await tryCancelBankIdAuth(cancelOrderRef)
			}
		}
		tryStartBankIdAuth()
	}, [])

	useEffect(() => {
		if(remainingTime > 240)
			setRemainingTimeMessage(t('QRCodeTimeLeftInMinutes', {timeLeft: 5}))
		else if (remainingTime > 180)
			setRemainingTimeMessage(t('QRCodeTimeLeftInMinutes', {timeLeft: 4}))
		else if (remainingTime > 120)
			setRemainingTimeMessage(t('QRCodeTimeLeftInMinutes', {timeLeft: 3}))
		else if (remainingTime > 60)
			setRemainingTimeMessage(t('QRCodeTimeLeftInMinutes', {timeLeft: 2}))
		else if(remainingTime > 50)
			setRemainingTimeMessage(t('QRCodeTimeLeftInMinutes', {timeLeft: 1}))
		else if (remainingTime > 40)
			setRemainingTimeMessage(t('QRCodeTimeLeftInSeconds', {timeLeft: 50}))
		else if (remainingTime > 30)
			setRemainingTimeMessage(t('QRCodeTimeLeftInSeconds', {timeLeft: 40}))
		else if (remainingTime > 20)
			setRemainingTimeMessage(t('QRCodeTimeLeftInSeconds', {timeLeft: 30}))
		else if (remainingTime > 10)
			setRemainingTimeMessage(t('QRCodeTimeLeftInSeconds', {timeLeft: 20}))
		else if (remainingTime > 0)
			setRemainingTimeMessage(t('QRCodeTimeLeftInSeconds', {timeLeft: remainingTime}))
		else
			setRemainingTimeMessage(t('QRCodeTimeLeftInSeconds', {timeLeft: 0}))
	}, [remainingTime, t])

	return (
		<div className="bankid-se-container">
			<div className="d-flex flex-column align-items-center">
				<span>{t('QRCodeDescription')}</span>
				{error && <ErrorBox message={error.message} css="mt-2" />}
				{remainingTime > 0 ? <QRCodeWrapper
					qrCodeData={qrCodeData}
				/> : <ErrorBox message={t('QRCodeTimeoutMessage')} css="mt-2" />}
				<div aria-live='polite'>{remainingTimeMessage}</div>
			</div>
		</div>
	)
}

export default BankIdSeQRContentBlock
